import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { apiFetch } from '../api/client';
import { getScenarioForMission, loadRuntimeScenario } from '../data/scenarios';
import { buildNavSequence, useItemNav, type SeqItem } from '../utils/itemNav';

/** Refresh the server-gated navigation after a scene/card visit is confirmed. */
export function useVisitedItemNav(route: any, navigation: any, displayedId?: string | number, onProgressConfirmed?: () => void) {
  const missionId = Number(route.params?.missionId);
  const itemId = String(displayedId ?? route.params?.itemId ?? '');
  const identity = `${missionId}:${itemId}`;
  const needsRefresh = !!missionId && !!itemId && !getScenarioForMission(missionId)?.testMode;
  const { getAccessToken } = useAuth();
  const { isItemCompleted } = useStoryProgress();
  const completed = isItemCompleted(missionId, itemId);
  const [fresh, setFresh] = useState<{ identity: string; sequence: SeqItem[] } | null>(null);
  const [retry, setRetry] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (!missionId || !itemId || !completed) return;
    void (async () => {
      try {
        const token = getAccessToken();
        if (!token) throw new Error('Session unavailable');
        await loadRuntimeScenario(missionId,
          () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }), true);
        if (!cancelled) {
          setFresh({ identity, sequence: buildNavSequence(getScenarioForMission(missionId)?.chapters ?? []) });
          onProgressConfirmed?.();
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [identity, missionId, itemId, completed, getAccessToken, retry, onProgressConfirmed]);
  const sequence = fresh?.identity === identity ? fresh.sequence : null;
  const nav = useItemNav(sequence ? { ...route, params: { ...route.params,
    itemSeq: sequence, itemIndex: sequence.findIndex(item => String(item.id) === itemId),
  } } : route, navigation, displayedId,
    buildNavSequence(getScenarioForMission(missionId)?.chapters ?? []));
  return {
    ...nav,
    // Never navigate with stale shells while refreshing. A failed refresh can be retried.
    goNext: !needsRefresh || sequence ? nav.goNext : failed ? () => setRetry(value => value + 1) : undefined,
    isLast: !needsRefresh || sequence ? nav.isLast : false,
  };
}
